import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  FileText,
  Folder,
  FolderPlus,
  FolderInput,
  Plus,
  Search,
  Tag,
  Pencil,
  Trash2,
  Eye,
  Download,
  ChevronDown,
  Building2,
  Users,
} from 'lucide-react';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../contexts/ResponsiveContext';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { getTemplateViewUrl } from '../../utils/hrTemplates';
import { getPartners, getPartner, updatePartner } from '../../services/partnersService';
import {
  newId,
  DOC_UPLOAD_ACCEPT,
  DOC_TAGS,
  serializeDocumentsForFirestore,
  normalizeDocFromPartner,
  serializeFoldersForFirestore,
  normalizeFoldersFromPartner,
  uploadPartnerDocumentFile,
} from '../../utils/partnerDocuments';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const formatDate = (d) => {
  if (!d) return '—';
  const date = d instanceof Date ? d : d.toDate ? d.toDate() : new Date(d);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const partnerInitial = (p) => (p?.companyName || '?').trim()[0]?.toUpperCase() || '?';

/**
 * My Documents — the partner member's side of the documents kept on their
 * partner record. Whatever the internal team uploads on the Partner Overview
 * Documents tab shows here, and whatever the partner member uploads here shows
 * there. Same Firestore doc, same storage folder.
 */
const MyDocuments = () => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const { isMobile } = useResponsive();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [myPartners, setMyPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [partner, setPartner] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [search, setSearch] = useState('');

  const [viewerDoc, setViewerDoc] = useState(null);
  const [editDocId, setEditDocId] = useState(null);
  const [docNameDraft, setDocNameDraft] = useState('');
  const [moveDocId, setMoveDocId] = useState(null);
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [folderModal, setFolderModal] = useState(null);
  const [folderName, setFolderName] = useState('');

  // Which partner records is this user a member of?
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await getPartners();
        const mine = all.filter((p) => (p.members || []).some((m) => m.userId === currentUser.uid));
        if (cancelled) return;
        setMyPartners(mine);
        setPartnerId((prev) => prev || mine[0]?.id || '');
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Failed to load your partner record.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Load the selected partner's documents + folders (raw doc, so folders and
  // per-doc folder/tag fields come through unchanged).
  const load = useCallback(async () => {
    if (!partnerId) return;
    try {
      const [normalized, rawSnap] = await Promise.all([
        getPartner(partnerId),
        getDoc(doc(db, 'partners', partnerId)),
      ]);
      const raw = rawSnap.exists() ? rawSnap.data() : {};
      setPartner(normalized);
      setDocuments(Array.isArray(raw.documents) ? raw.documents.map(normalizeDocFromPartner) : []);
      setFolders(normalizeFoldersFromPartner(raw.documentFolders));
      setError(null);
    } catch (e) {
      console.error(e);
      setError('Failed to load documents.');
    }
  }, [partnerId]);

  useEffect(() => {
    setCurrentFolderId(null);
    setTagFilter(null);
    setSearch('');
    load();
  }, [load]);

  // ---- Persistence (mirrors PartnerOverview so both sides stay in step) ----
  const persistDocuments = async (nextDocs) => {
    await updatePartner(partnerId, { documents: serializeDocumentsForFirestore(nextDocs) });
  };

  const handleUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = DOC_UPLOAD_ACCEPT;
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file || !partnerId) return;
      setUploading(true);
      setError(null);
      try {
        const docId = newId();
        const url = await uploadPartnerDocumentFile(partnerId, docId, file);
        const u = auth.currentUser;
        const entry = {
          id: docId,
          name: file.name || 'Document',
          fileName: file.name || '',
          fileUrl: url,
          folderId: currentFolderId || '',
          tagIds: [],
          uploadedAt: new Date(),
          uploadedByUid: u?.uid || '',
          uploaderName: u?.displayName || u?.email || 'Unknown',
        };
        const next = [...documents, entry];
        setDocuments(next);
        await persistDocuments(next);
        setEditDocId(entry.id);
        setDocNameDraft(entry.name);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const commitRename = async () => {
    const name = docNameDraft.trim();
    const target = documents.find((d) => d.id === editDocId);
    if (!target || !name || name === target.name) return;
    const next = documents.map((d) => (d.id === editDocId ? { ...d, name } : d));
    setDocuments(next);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to rename document');
      await load();
    }
  };

  const toggleTag = async (docId, tagId) => {
    const next = documents.map((d) => {
      if (d.id !== docId) return d;
      const has = (d.tagIds || []).includes(tagId);
      return { ...d, tagIds: has ? d.tagIds.filter((x) => x !== tagId) : [...(d.tagIds || []), tagId] };
    });
    setDocuments(next);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to update tags');
      await load();
    }
  };

  const moveToFolder = async (docId, folderId) => {
    const next = documents.map((d) => (d.id === docId ? { ...d, folderId } : d));
    setDocuments(next);
    setMoveDocId(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to move document');
      await load();
    }
  };

  const removeDocument = async (docId) => {
    const next = documents.filter((d) => d.id !== docId);
    setDocuments(next);
    setDeleteDocId(null);
    try {
      await persistDocuments(next);
    } catch (err) {
      console.error(err);
      setError('Failed to remove document');
      await load();
    }
  };

  const saveFolder = async () => {
    const name = folderName.trim();
    if (!name || !partnerId) return;
    const u = auth.currentUser;
    const next = folderModal?.id
      ? folders.map((f) => (f.id === folderModal.id ? { ...f, name } : f))
      : [...folders, { id: newId(), name, createdByName: u?.displayName || u?.email || 'Unknown', createdAt: new Date() }];
    setFolders(next);
    setFolderModal(null);
    try {
      await updatePartner(partnerId, { documentFolders: serializeFoldersForFirestore(next) });
    } catch (err) {
      console.error(err);
      setError('Failed to save folder');
      await load();
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Its documents move back to All files.')) return;
    const nextFolders = folders.filter((f) => f.id !== folderId);
    const nextDocs = documents.map((d) => (d.folderId === folderId ? { ...d, folderId: '' } : d));
    setFolders(nextFolders);
    setDocuments(nextDocs);
    if (currentFolderId === folderId) setCurrentFolderId(null);
    try {
      await updatePartner(partnerId, {
        documentFolders: serializeFoldersForFirestore(nextFolders),
        documents: serializeDocumentsForFirestore(nextDocs),
      });
    } catch (err) {
      console.error(err);
      setError('Failed to delete folder');
      await load();
    }
  };

  const openViewer = (d) => {
    const url = getTemplateViewUrl({ fileUrl: d.fileUrl, fileName: d.fileName || d.name });
    if (url) setViewerDoc({ url, title: d.name });
  };

  const downloadDoc = (d) => {
    if (d.fileUrl) window.open(d.fileUrl, '_blank', 'noopener');
  };

  // ---- Derived view ----
  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const inFolder = folders.find((f) => f.id === currentFolderId) || null;
  const visibleDocs = useMemo(
    () =>
      documents.filter((d) => {
        if (q && !(d.name || '').toLowerCase().includes(q)) return false;
        if (tagFilter) return (d.tagIds || []).includes(tagFilter);
        if (searching) return true;
        return (d.folderId || '') === (currentFolderId || '');
      }),
    [documents, q, tagFilter, searching, currentFolderId]
  );
  const docCountIn = (folderId) => documents.filter((d) => (d.folderId || '') === folderId).length;
  const showFolders = !tagFilter && !searching;

  // ---- Styles ----
  const surface = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const inputBase = `w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
  }`;
  const secondaryBtn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors';
  const primaryBtn = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white shadow-sm transition-colors';
  const rowIconBtn = 'p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-800 transition-colors';
  const rowIconBtnDanger = 'p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-colors';
  const folderChip = (active) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      active
        ? 'bg-red-600 border-red-600 text-white'
        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`;
  const modalCancelBtn = `px-4 py-2 rounded-xl text-sm font-medium ${darkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} />
          <p className={`mt-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading your documents…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isMobile ? 'p-3' : 'p-6'}`}>
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="relative flex items-center justify-between gap-3 mb-6 min-h-[44px]">
          <BackButton to="/crm" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 dark:text-white whitespace-nowrap`}>
              My Documents
            </h1>
          </div>
          {partnerId && (
            <button type="button" disabled={uploading} onClick={handleUploadClick} className={primaryBtn}>
              <Plus className="w-4 h-4" /> {uploading ? 'Uploading…' : isMobile ? 'Add' : 'Add document'}
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {myPartners.length === 0 ? (
          <div className={`${surface} rounded-2xl shadow-sm p-12 text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">You're not linked to a partner yet</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Shared documents live on your partner record. Ask an owner or executive to add you as a team member on
              the Partners page, and your files will show up here.
            </p>
          </div>
        ) : (
          <>
            {/* Partner banner (+ switcher when a user belongs to more than one) */}
            <div className={`${surface} rounded-2xl shadow-sm p-4 mb-5 flex items-center gap-4 flex-wrap`}>
              <div className="w-14 h-14 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 border border-gray-200 dark:border-gray-700">
                {partner?.logoUrl ? (
                  <img src={partner.logoUrl} alt="" className="max-w-[85%] max-h-[85%] object-contain" />
                ) : (
                  <span className="text-xl font-bold text-gray-500 dark:text-gray-300">{partnerInitial(partner)}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" /> Partner
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white truncate">{partner?.companyName || '…'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {documents.length} file{documents.length === 1 ? '' : 's'} · {folders.length} folder{folders.length === 1 ? '' : 's'}
                </p>
              </div>
              {myPartners.length > 1 && (
                <div className="relative">
                  <select
                    value={partnerId}
                    onChange={(e) => setPartnerId(e.target.value)}
                    className={`${inputBase} pr-9 appearance-none cursor-pointer w-auto`}
                  >
                    {myPartners.map((p) => (
                      <option key={p.id} value={p.id}>{p.companyName}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search documents…"
                  className={`${inputBase} pl-10 shadow-sm`}
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  <select
                    value={tagFilter || 'all'}
                    onChange={(e) => setTagFilter(e.target.value === 'all' ? null : e.target.value)}
                    className={`${secondaryBtn} pl-8 pr-8 appearance-none cursor-pointer`}
                    title="Filter by tag"
                  >
                    <option value="all">All tags</option>
                    {DOC_TAGS.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <button
                  type="button"
                  onClick={() => { setFolderModal({ id: null }); setFolderName(''); }}
                  className={secondaryBtn}
                >
                  <FolderPlus className="w-4 h-4" /> New folder
                </button>
              </div>
            </div>

            {/* Folder chips */}
            {showFolders && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <button type="button" onClick={() => setCurrentFolderId(null)} className={folderChip(!currentFolderId)}>
                  <FileText className="w-3.5 h-3.5" /> All files
                  <span className={`text-xs ${!currentFolderId ? 'text-red-100' : 'text-gray-400'}`}>{docCountIn('')}</span>
                </button>
                {folders.map((f) => (
                  <div key={f.id} className="group relative">
                    <button type="button" onClick={() => setCurrentFolderId(f.id)} className={folderChip(currentFolderId === f.id)}>
                      <Folder className={`w-3.5 h-3.5 ${currentFolderId === f.id ? 'text-white' : 'text-amber-500'}`} /> {f.name}
                      <span className={`text-xs ${currentFolderId === f.id ? 'text-red-100' : 'text-gray-400'}`}>{docCountIn(f.id)}</span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Current folder actions */}
            {inFolder && showFolders && (
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Viewing <span className="font-semibold text-gray-900 dark:text-white">{inFolder.name}</span>
                  {inFolder.createdByName && <span className="text-gray-400"> · created by {inFolder.createdByName}</span>}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => { setFolderModal({ id: inFolder.id }); setFolderName(inFolder.name); }}
                    className={rowIconBtn}
                    title="Rename folder"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => deleteFolder(inFolder.id)} className={rowIconBtnDanger} title="Delete folder">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Documents */}
            {visibleDocs.length === 0 ? (
              <div className={`${surface} rounded-2xl shadow-sm p-12 text-center`}>
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searching
                    ? 'No documents match your search.'
                    : tagFilter
                      ? 'No documents with this tag.'
                      : inFolder
                        ? 'This folder is empty.'
                        : 'No documents yet. Add one to share it with the BBA team.'}
                </p>
              </div>
            ) : (
              <div className={`${surface} rounded-2xl shadow-sm overflow-hidden`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                        <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Tags</th>
                        {!isMobile && (
                          <>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">Uploaded by</th>
                            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">Uploaded</th>
                          </>
                        )}
                        <th className="px-4 py-3 w-40" aria-hidden />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {visibleDocs.map((d) => {
                        const folder = folders.find((f) => f.id === d.folderId);
                        return (
                          <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                            <td className="px-4 py-3">
                              <button type="button" onClick={() => openViewer(d)} className="flex items-center gap-2.5 text-left min-w-0 group">
                                <span className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                                  <FileText className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                                </span>
                                <span className="min-w-0">
                                  <span className="block font-medium text-gray-900 dark:text-white truncate max-w-[280px] group-hover:underline">{d.name}</span>
                                  {(searching || tagFilter) && folder && (
                                    <span className="block text-xs text-gray-400 truncate">in {folder.name}</span>
                                  )}
                                </span>
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(d.tagIds || []).map((tid) => {
                                  const t = DOC_TAGS.find((x) => x.id === tid);
                                  return t ? (
                                    <span key={tid} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white" style={{ backgroundColor: t.color }}>
                                      {t.name}
                                    </span>
                                  ) : null;
                                })}
                                {(d.tagIds || []).length === 0 && <span className="text-xs text-gray-400">—</span>}
                              </div>
                            </td>
                            {!isMobile && (
                              <>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{d.uploaderName || '—'}</td>
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDate(d.uploadedAt)}</td>
                              </>
                            )}
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-0.5">
                                <button type="button" onClick={() => openViewer(d)} className={rowIconBtn} title="View">
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => downloadDoc(d)} className={rowIconBtn} title="Download">
                                  <Download className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => { setEditDocId(d.id); setDocNameDraft(d.name || ''); }} className={rowIconBtn} title="Rename & tags">
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => setMoveDocId(d.id)} className={rowIconBtn} title="Move to folder">
                                  <FolderInput className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => setDeleteDocId(d.id)} className={rowIconBtnDanger} title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fullscreen viewer */}
      <DocumentViewerModal isOpen={!!viewerDoc} onClose={() => setViewerDoc(null)} url={viewerDoc?.url} title={viewerDoc?.title} />

      {/* New / rename folder */}
      <Modal isOpen={!!folderModal} onClose={() => setFolderModal(null)} maxWidth="md">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
          {folderModal?.id ? 'Rename folder' : 'New folder'}
        </h2>
        <input
          autoFocus
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveFolder(); } }}
          placeholder="Folder name"
          className={inputBase}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={() => setFolderModal(null)} className={modalCancelBtn}>Cancel</button>
          <button type="button" onClick={saveFolder} disabled={!folderName.trim()} className={primaryBtn}>
            {folderModal?.id ? 'Save' : 'Create'}
          </button>
        </div>
      </Modal>

      {/* Rename + tags */}
      <Modal isOpen={!!editDocId} onClose={async () => { await commitRename(); setEditDocId(null); }} maxWidth="md">
        {(() => {
          const target = documents.find((d) => d.id === editDocId);
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Rename &amp; tags</h2>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400">Name</label>
              <input
                autoFocus
                value={docNameDraft}
                onChange={(e) => setDocNameDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                placeholder="Document name"
                className={`${inputBase} mb-5`}
              />
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-gray-500 dark:text-gray-400">Tags</label>
              <div className="flex flex-wrap gap-2">
                {DOC_TAGS.map((t) => {
                  const on = (target?.tagIds || []).includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => target && toggleTag(target.id, t.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        on ? 'text-white border-transparent' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                      style={on ? { backgroundColor: t.color } : undefined}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: on ? 'rgba(255,255,255,0.8)' : t.color }} />
                      {t.name}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-5">
                <button type="button" onClick={async () => { await commitRename(); setEditDocId(null); }} className={primaryBtn}>Done</button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Move to folder */}
      <Modal isOpen={!!moveDocId} onClose={() => setMoveDocId(null)} maxWidth="md">
        {(() => {
          const target = documents.find((d) => d.id === moveDocId);
          const option = 'w-full text-left px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-default text-gray-900 dark:text-white';
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 text-center">Move to folder</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center truncate">{target?.name}</p>
              <div className="space-y-1">
                <button type="button" disabled={!(target?.folderId || '')} onClick={() => target && moveToFolder(target.id, '')} className={option}>
                  <FileText className="w-4 h-4 text-gray-400" /> All files (no folder)
                </button>
                {folders.map((f) => (
                  <button key={f.id} type="button" disabled={target?.folderId === f.id} onClick={() => target && moveToFolder(target.id, f.id)} className={option}>
                    <Folder className="w-4 h-4 text-amber-500" /> {f.name}
                  </button>
                ))}
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteDocId} onClose={() => setDeleteDocId(null)} maxWidth="md">
        {(() => {
          const target = documents.find((d) => d.id === deleteDocId);
          return (
            <>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">Delete document?</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 text-center">
                “{target?.name || 'This document'}” will be removed for both you and the BBA team. This can't be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setDeleteDocId(null)} className={modalCancelBtn}>Cancel</button>
                <button type="button" onClick={() => removeDocument(deleteDocId)} className={primaryBtn}>Delete</button>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default MyDocuments;
